<template>
  <div class="c-gen-nav__wrapper" v-if="defaultTheme !== 'transparent'" />
  <header class="c-gen-nav" :class="theme" v-if="fields">
    <div class="c-gen-nav__top">
      <div class="c-gen-nav__grid">
        <div class="c-gen-nav__logo">
          <SiteLink :link="createSiteLink('/')">
            <Icon v-if="hasLogoSvg" :field="fields.logo" class="c-gen-nav__logo" />
            <SiteImage
              v-else-if="fields.logo"
              :field="fields.logo"
              loading="eager"
              class="c-gen-nav__logo"
            />
          </SiteLink>
        </div>

        <!-- Desktop menu -->
        <HeaderDesktop v-bind="props" />

        <!-- Desktop search box -->
        <div class="c-gen-nav__search" v-if="showSearchBox">
          <div class="c-gen-nav__search-box">
            <input
              type="text"
              :placeholder="fields.searchPlaceholder.value"
              v-model="searchQuery"
              @focus="showSearchResults = true"
              @blur="handleBlur"
              @keyup.enter="handleSearchIconClick"
            />
            <button class="c-gen-nav__search-btn" @click.stop="handleSearchIconClick">
              <Icon name="search" class="c-gen-nav__icon c-gen-nav__search-icon" />
            </button>
          </div>

          <!-- Search results dropdown -->
          <div
            class="c-gen-nav__search-results"
            v-show="showSearchResults && searchQuery.trim()"
            @mouseenter="handleSearchResultsMouseEnter"
            @mouseleave="handleSearchResultsMouseLeave"
          >
            <!-- Empty state -->
            <div v-if="filteredResults.length === 0" class="c-gen-nav__search-empty">
              {{ fields.searchEmpty.value }}
            </div>

            <!-- Regular results -->
            <div class="c-gen-nav__search-normal" v-if="filteredResults.length > 0">
              <SiteLink
                class="c-gen-nav__search-item"
                v-for="(item, index) in normalResults"
                :key="'norm-' + index"
                :link="createSiteLink((item as any).url)"
                @click.stop="handleSearchResultClick((item as any).url)"
              >
                <JssRichText :field="{ value: (item as any).title }" />
              </SiteLink>
            </div>
          </div>
        </div>

        <!-- Mobile search box -->
        <div class="c-gen-nav__mobile-search-box" v-if="showMobileSearch">
          <div class="c-gen-nav__mobile-search-input-wrap">
            <input
              type="text"
              :placeholder="fields.searchPlaceholder.value"
              v-model="searchQuery"
              @focus="showSearchResults = true"
              @blur="handleBlur"
              @keyup.enter="handleSearchIconClick"
              ref="mobileSearchInput"
            />
            <button class="c-gen-nav__search-btn" @click.stop="handleSearchIconClick">
              <Icon name="search" class="c-gen-nav__icon c-gen-nav__search-icon" />
            </button>
          </div>

          <!-- Search results dropdown -->
          <div
            class="c-gen-nav__search-results"
            v-show="showSearchResults && searchQuery.trim()"
            @mouseenter="handleSearchResultsMouseEnter"
            @mouseleave="handleSearchResultsMouseLeave"
          >
            <div v-if="filteredResults.length === 0" class="c-gen-nav__search-empty">
              {{ fields.searchEmpty.value }}
            </div>

            <div class="c-gen-nav__search-normal" v-if="filteredResults.length > 0">
              <SiteLink
                class="c-gen-nav__search-item"
                v-for="(item, index) in normalResults"
                :key="'norm-' + index"
                :link="createSiteLink((item as any).url)"
                @click.stop="handleSearchResultClick((item as any).url)"
              >
                <JssRichText :field="{ value: (item as any).title }" />
              </SiteLink>
            </div>
          </div>
        </div>

        <div class="c-gen-nav__actions">
          <!-- Mobile search icon -->
          <button class="c-gen-nav__mobile-search" @click="openMobileSearch">
            <Icon name="search" class="c-gen-nav__icon" />
          </button>

          <!-- Desktop search icon -->
          <button class="c-gen-nav__desktop-search" v-if="!showSearchBox" @click="toggleSearch">
            <Icon name="search" class="c-gen-nav__icon" />
          </button>

          <!-- User actions -->
          <div class="c-gen-nav__user" ref="userRef">
            <template v-if="isLoggedIn">
              <button
                class="c-gen-nav__user-toggle"
                type="button"
                :aria-expanded="userMenuOpen"
                @click.stop="toggleUserMenu"
              >
                <Icon name="user" class="c-gen-nav__icon" />
                <span>{{ userDisplayName }}</span>
              </button>
              <div v-show="userMenuOpen" class="c-gen-nav__user-menu">
                <button
                  class="c-gen-nav__user-menu-item c-gen-nav__user-menu-item--primary"
                  type="button"
                  @click="goMyAccount"
                >
                  My Account
                </button>
                <button class="c-gen-nav__user-menu-item" type="button" @click="handleSignOut">
                  Sign Out
                </button>
              </div>
            </template>
            <template v-else>
              <Icon name="user" class="c-gen-nav__icon" />
              <SiteLink
                v-if="getLinkHref(fields.signLink)"
                :link="fields.signLink"
                v-bind="getLinkAttrs(fields.signLink)"
              >
                Sign in
              </SiteLink>
              |
              <SiteLink
                v-if="getLinkHref(fields.registerLink)"
                :link="fields.registerLink"
                v-bind="getLinkAttrs(fields.registerLink)"
              >
                Register
              </SiteLink>
            </template>
          </div>

          <!-- Language switch -->
          <div class="c-gen-nav__language" ref="languageRef">
            <button
              class="c-gen-nav__language-toggle"
              type="button"
              :aria-expanded="languageOpen"
              aria-label="Switch language"
              @click="toggleLanguage"
            >
              <span class="c-gen-nav__language-globe" aria-hidden="true"></span>
              <span>{{ currentLanguageLabel }}</span>
              <Icon name="caretDown" class="c-gen-nav__language-arrow" />
            </button>
            <div v-show="languageOpen" class="c-gen-nav__language-menu">
              <SiteLink
                v-for="item in languageOptions"
                :key="item.code || item.label"
                class="c-gen-nav__language-option"
                :class="{ 'is-active': isCurrentLanguage(item.code) }"
                :link="item.link"
                @click="languageOpen = false"
              >
                {{ item.label }}
              </SiteLink>
            </div>
          </div>

          <!-- Mobile menu button -->
          <button class="c-gen-nav__mobile-toggle" @click="toggleMobileMenu">
            <Icon name="menu" class="c-gen-nav__icon c-gen-nav__icon--large" />
          </button>
        </div>
      </div>
    </div>

    <HeaderMobile :menus="menus" :isOpen="mobileMenuOpen" @close="mobileMenuOpen = false" />
  </header>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { HeaderProps } from '@/types/components/Header';
import HeaderDesktop from './HeaderDesktop.vue';
import HeaderMobile from './HeaderMobile.vue';
import SiteImage from '@/elements/SiteImage.vue';
import SiteLink from '@/elements/SiteLink.vue';
import { useI18n } from 'vue-i18n';
import useScrollY from '@/hooks/useScrollY';
import utils from '@/utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import * as querystring from 'querystring';
import api from '@/api/index';
import { readMyAccountAuth } from '@/utils/myaccount-auth-reader';
import { webStorage } from '@/utils/web-storage';
import { S_LOGIN_INFO, S_USER_INFO } from '@/utils/web-storage-keys';
import { clearCookies } from '@/utils/cookies-util';

const { scrollTop } = useScrollY();
const props = defineProps<HeaderProps>();
const route = useRoute();
const hasLogoSvg = computed(() => Boolean(props.fields?.logo?.value?.svgCode));

const defaultTheme = computed(
  () => utils.sp.getVal(props.page?.fields?.headerTheme) ?? 'transparent'
);

const theme = computed(() => {
  return scrollTop.value > 60 ? 'white' : defaultTheme.value;
});

const { t } = useI18n();
const menus = computed(() => props.fields?.menus ?? []);

const languageRef = ref<HTMLElement | null>(null);
const languageOpen = ref(false);
const userRef = ref<HTMLElement | null>(null);
const userMenuOpen = ref(false);
const authInfo = ref<any>({ isLoggedIn: false, source: null });
type LanguageOption = {
  code: string;
  label: string;
  href: string;
  link: any;
};

const languageOptions = computed<LanguageOption[]>(() => {
  return (props.fields?._languages ?? [])
    .map((item: any) => {
      const fields = item?.fields || item || {};
      const link = fields.link;
      const href = getLinkHref(link);

      return {
        code: utils.sc.getFieldValue(item, 'code') || '',
        label: utils.sc.getFieldValue(item, 'label') || '',
        href,
        link,
      };
    })
    .filter((item) => item.label && item.href);
});

const getPathname = (url: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) {
    try {
      return new URL(url).pathname;
    } catch (error) {
      return url;
    }
  }

  return url.split(/[?#]/)[0];
};

const normalizePathname = (url: string) => {
  const pathname = getPathname(url) || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '').toLowerCase() : pathname;
};

const getCurrentLanguage = () => {
  if (typeof route.params.lang === 'string') return route.params.lang;

  if (typeof window !== 'undefined') {
    const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    if (match?.[1]) return match[1];
  }

  return props.page?.itemLanguage || props.page?.language || 'en';
};

const normalizeLanguage = (language?: string) => language?.toLowerCase() || '';

const getLanguageBase = (language?: string) => normalizeLanguage(language).split('-')[0] || '';

const currentLanguageOption = computed(() => {
  const currentPathname =
    route.path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const matchedByPath = languageOptions.value.find(
    (item) => normalizePathname(item.href) === normalizePathname(currentPathname)
  );

  if (matchedByPath) return matchedByPath;

  const currentLanguage = getCurrentLanguage();
  const currentLanguageKey = normalizeLanguage(currentLanguage);
  const currentLanguageBase = getLanguageBase(currentLanguage);

  return (
    languageOptions.value.find(
      (item) =>
        normalizeLanguage(item.code) === currentLanguageKey ||
        getLanguageBase(item.code) === currentLanguageBase
    ) || null
  );
});

const currentLanguageLabel = computed(() => {
  return (
    currentLanguageOption.value?.label ||
    getLanguageBase(getCurrentLanguage()).toUpperCase() ||
    'EN'
  );
});

const isCurrentLanguage = (language: string) => {
  if (currentLanguageOption.value?.code) {
    return normalizeLanguage(currentLanguageOption.value.code) === normalizeLanguage(language);
  }

  const currentLanguage = getCurrentLanguage();
  return (
    normalizeLanguage(currentLanguage) === normalizeLanguage(language) ||
    getLanguageBase(currentLanguage) === getLanguageBase(language)
  );
};

const toggleLanguage = () => {
  languageOpen.value = !languageOpen.value;
};

const closeLanguageOnOutsideClick = (event: MouseEvent) => {
  if (!languageRef.value?.contains(event.target as Node)) {
    languageOpen.value = false;
  }
};

const refreshAuthInfo = () => {
  authInfo.value = readMyAccountAuth();
};

const isLoggedIn = computed(() => Boolean(authInfo.value?.isLoggedIn));

const userDisplayName = computed(() => {
  const profile = authInfo.value?.profile || {};
  const user = authInfo.value?.user || {};
  return (
    profile.first_name || user.first_name || user.nickname || authInfo.value?.email || 'My Account'
  );
});

const myAccountUrl = computed(() => {
  return '/myportal/myporta';
});

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
};

const closeUserMenuOnOutsideClick = (event: MouseEvent) => {
  if (!userRef.value?.contains(event.target as Node)) {
    userMenuOpen.value = false;
  }
};

const goMyAccount = () => {
  userMenuOpen.value = false;
  if (typeof window !== 'undefined') {
    window.location.href = myAccountUrl.value;
  }
};

const handleSignOut = () => {
  webStorage.clear(S_USER_INFO);
  webStorage.clear(S_LOGIN_INFO);

  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('S_USER_INFO');
    window.localStorage.removeItem('S_LOGIN_INFO');
  }

  clearCookies('S_USER_SESSION');
  clearCookies('7e2411adf07bfb27e6326b47734bead7');
  authInfo.value = { isLoggedIn: false, source: null };
  userMenuOpen.value = false;

  const loginUrl = getLinkHref(props.fields?.signLink) || '/sign';
  if (typeof window !== 'undefined') {
    window.location.href = loginUrl;
  }
};

onMounted(() => {
  refreshAuthInfo();
  document.addEventListener('click', closeLanguageOnOutsideClick);
  document.addEventListener('click', closeUserMenuOnOutsideClick);
  window.addEventListener('storage', refreshAuthInfo);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeLanguageOnOutsideClick);
  document.removeEventListener('click', closeUserMenuOnOutsideClick);
  window.removeEventListener('storage', refreshAuthInfo);
});

const getLinkHref = (link: any) => {
  let url = link?.value?.href || link?.href || '';

  if (!url) return '';
  if (/^(mailto|tel):/gi.test(url)) return url;
  if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) return url.replace(/^https?:\/\//gi, '');

  const queryString = link?.value?.querystring || link?.querystring;
  if (queryString) {
    try {
      url = appendQuery(url, querystring.parse(queryString));
    } catch (error) {
      console.warn('Failed to parse querystring:', queryString, error);
    }
  }

  const anchor = link?.value?.anchor || link?.anchor;
  if (anchor) {
    url = `${url}?id=${anchor}`;
  }

  return getBetterUrl(url) ?? '';
};

const getLinkText = (link: any) => {
  return link?.value?.text || link?.text || getLinkHref(link);
};

const getLinkAttrs = (link: any) => {
  const value = link?.value || link || {};
  const attrs: Record<string, string> = {};

  if (value.target) attrs.target = value.target;
  if (value.title) attrs.title = value.title;
  if (value.class) attrs.class = value.class;
  if (value.target === '_blank') attrs.rel = 'noopener noreferrer';

  return attrs;
};

const createSiteLink = (href: string) => ({
  value: {
    href: href || '',
  },
});

// Mobile Menu State
const mobileMenuOpen = ref(false);
const showSearchBox = ref(false); // Control desktop search box visibility
const showMobileSearch = ref(false); // Control mobile search box visibility
const mobileSearchInput = ref(null);

// Search State
const searchQuery = ref('');
const showSearchResults = ref(false);
const searchResults = ref<any[]>([]);
const isSearching = ref(false);
let hideTimer: number | null = null;
let searchTimer: number | null = null;
let searchRequestId = 0;
const isMouseOverResults = ref(false);

// API search results
const filteredResults = computed(() => {
  return searchResults.value;
});

// Category results
const categoryResults = computed(() => {
  return [];
});

// Normal results
const normalResults = computed(() => {
  return searchResults.value;
});

// Search handling - watch searchQuery with 500ms debounce
watch(searchQuery, (newVal) => {
  const keyword = newVal.trim();

  if (!keyword) {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  if (searchTimer) {
    clearTimeout(searchTimer);
  }

  searchTimer = window.setTimeout(async () => {
    searchTimer = null;
    const currentRequestId = ++searchRequestId;
    isSearching.value = true;

    try {
      const [res, error] = await api.search.getSearch({
        q: keyword,
        page: 1,
        limit: 1000,
      });

      // Discard stale response if a newer request was fired
      if (currentRequestId !== searchRequestId) {
        return;
      }

      if (res) {
        const contentList = res.content?.list || [];
        const webinarList = res.webinar?.list || [];
        searchResults.value = [...contentList, ...webinarList];
      } else {
        searchResults.value = [];
      }
    } catch (e) {
      if (currentRequestId !== searchRequestId) {
        return;
      }
      searchResults.value = [];
    } finally {
      if (currentRequestId === searchRequestId) {
        isSearching.value = false;
        showSearchResults.value = true;
      }
    }
  }, 500);
});

const handleBlur = () => {
  // If the mouse is over the results dropdown (e.g., user is scrolling/hovering),
  // do not hide the dropdown even if the input loses focus.
  if (isMouseOverResults.value) {
    return;
  }
  hideTimer = window.setTimeout(() => {
    showSearchResults.value = false;
    searchQuery.value = '';
    showSearchBox.value = false;
    showMobileSearch.value = false;
  }, 200);
};

const handleSearchResultsMouseEnter = () => {
  isMouseOverResults.value = true;
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
};

const handleSearchResultsMouseLeave = () => {
  isMouseOverResults.value = false;
  hideTimer = window.setTimeout(() => {
    showSearchResults.value = false;
    searchQuery.value = '';
    showSearchBox.value = false;
    showMobileSearch.value = false;
  }, 200);
};

const handleSearchIconClick = () => {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
  showSearchResults.value = false;

  const keyword = searchQuery.value;
  searchQuery.value = '';
  showSearchBox.value = false;
  showMobileSearch.value = false;

  const searchLink = props?.fields?.searchLink ? getLinkHref(props.fields.searchLink) : '';
  if (searchLink) {
    const url = new URL(searchLink, window.location.origin);
    if (keyword) {
      url.searchParams.set('q', keyword);
    }
    window.location.href = url.toString();
  }
};

const handleSearchResultClick = (url: string) => {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
  showSearchResults.value = false;
  searchQuery.value = '';
  showSearchBox.value = false;
  showMobileSearch.value = false;

  if (url) {
    window.location.href = url;
  }
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const toggleSearch = () => {
  showSearchBox.value = !showSearchBox.value;
  if (!showSearchBox.value) {
    searchQuery.value = '';
    showSearchResults.value = false;
  }
};

const openMobileSearch = () => {
  showMobileSearch.value = true;
  nextTick(() => {
    if (mobileSearchInput.value) {
      (mobileSearchInput.value as HTMLInputElement).focus();
    }
  });
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-gen-nav {
  position: fixed;
  top: 0;
  z-index: 1000;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

  a,
  .e-site-link {
    text-decoration: none;
    color: inherit;
  }

  ul {
    list-style: none;
    //padding: 0;
    margin: 0;
  }

  &__wrapper {
    height: 54px;

    @include tablet-landscape {
      height: 58px;
    }
  }

  &__icon {
    width: 16px;
    height: 16px;
    display: inline-block;
    vertical-align: middle;

    &--large {
      width: 22px;
      height: 22px;
    }
  }

  &__grid {
    display: grid;
    gap: 0;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0 20px;
    align-items: center;

    @include tablet-landscape {
      padding: 0 32px;
    }

    @include desktop {
      padding: 0 40px 0 34px;
    }
  }

  &__top {
    position: relative;
    z-index: 1;
    // backdrop-filter: blur(18px);
    // -webkit-backdrop-filter: blur(18px);
    color: $white;
    padding: 14px 0;

    .c-gen-nav__grid {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }
  }

  &__logo {
    flex-shrink: 0;
    display: block;
    width: 92px;
    height: auto;
    @include desktop {
      width: 141px;
    }
    a,
    .e-site-link {
      display: flex;
      align-items: center;
    }

    .e-icon {
      width: 90px;
      height: auto;

      @include tablet-landscape {
        width: 120px;
      }

      @include desktop {
        width: 140px;
      }
    }
  }

  &__search {
    display: none;
    position: relative;

    @include tablet-landscape {
      display: flex;
      justify-content: center;
      flex: 1;
      max-width: 400px;
      margin: 0 16px;
    }

    .c-gen-nav__search-btn {
      display: flex;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      padding: 0;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &:hover {
        color: $white;
      }

      .c-gen-nav__search-icon {
        width: 18px;
        height: 18px;
      }
    }

    &-box {
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 24px;
      padding: 8px 16px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      width: 100%;
      gap: 12px;

      input {
        background: transparent;
        border: none;
        color: $white;
        flex: 1;
        padding: 0;
        outline: none;
        font-size: 14px;

        &::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }
      }
    }

    &__search-btn {
      display: flex;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      padding: 0;
      align-items: center;
      justify-content: center;

      &:hover {
        color: $white;
      }

      .c-gen-nav__search-icon {
        width: 18px;
        height: 18px;
      }
    }
  }

  &__mobile-search {
    display: flex;
    background: none;
    border: none;
    color: $white;
    cursor: pointer;
    padding: 2px;
    align-items: center;
    justify-content: center;

    @include tablet-landscape {
      display: none;
    }
  }

  &__mobile-search-box {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: $blue-night;
    padding: 12px 16px;
    z-index: 1000;

    @include tablet-landscape {
      display: none;
    }

    .c-gen-nav__search-results {
      position: static;
      transform: none;
      margin-top: 8px;
      max-height: 300px;
    }
  }

  &__mobile-search-input-wrap {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 24px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    gap: 12px;

    input {
      background: transparent;
      border: none;
      color: $white;
      flex: 1;
      padding: 0;
      outline: none;
      font-size: 14px;

      &::placeholder {
        color: rgba(255, 255, 255, 0.6);
      }
    }

    .c-gen-nav__search-btn {
      display: flex;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      cursor: pointer;
      padding: 0;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &:hover {
        color: $white;
      }

      .c-gen-nav__search-icon {
        width: 18px;
        height: 18px;
      }
    }
  }

  &__mobile-search-close {
    display: flex;
    background: none;
    border: none;
    color: $white;
    cursor: pointer;
    padding: 4px;
    align-items: center;
    justify-content: center;
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  &__mobile-toggle {
    display: flex;
    background: none;
    border: none;
    color: $white;
    cursor: pointer;
    padding: 2px;
    align-items: center;
    justify-content: center;

    @include tablet-landscape {
      display: none;
    }
  }

  &__desktop-search {
    display: none;
    background: none;
    border: none;
    color: $white;
    cursor: pointer;
    padding: 2px;
    align-items: center;
    justify-content: center;

    @include tablet-landscape {
      display: flex;
    }
  }

  &__language {
    display: none;

    @include tablet-landscape {
      position: relative;
      display: flex;
      align-items: center;
      color: $white;
      font-size: 12px;
      font-weight: 400;
      line-height: 1;
    }
  }

  &__language-toggle {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    line-height: 1;
    cursor: pointer;
  }

  &__language-globe {
    position: relative;
    width: 13px;
    height: 13px;
    border: 1.4px solid currentColor;
    border-radius: 50%;
    flex: 0 0 13px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      border-color: currentColor;
      border-style: solid;
    }

    &::before {
      top: 1px;
      bottom: 1px;
      left: 3.5px;
      width: 3px;
      border-width: 0 1px;
      border-radius: 50%;
    }

    &::after {
      top: 5px;
      left: 1px;
      width: 9px;
      border-width: 1px 0 0;
    }
  }

  &__language-arrow {
    width: 10px;
    height: 10px;
  }

  &__language-menu {
    position: absolute;
    top: calc(100% + 28px);
    right: -8px;
    display: grid;
    gap: 0;
    border-radius: 8px;
    background: rgba(242, 242, 242, 0.95);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
    color: #3d3d3f;
    z-index: 1002;
  }

  &__language-option {
    display: block;
    white-space: nowrap;
    width: 106px;
    padding: 14px 0;
    border-radius: 8px;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1.2;
    text-decoration: none;
    text-align: center;

    &:hover,
    &.is-active {
      color: #6d9875;
      font-weight: 700;
      background-color: rgba(241, 244, 241, 1);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    font-size: 12px;

    @include tablet {
      gap: 14px;
    }

    @include desktop {
      gap: 18px;
    }

    .c-gen-nav__user {
      display: none;
      position: relative;

      @include tablet-landscape {
        display: flex;
        align-items: center;
        gap: 6px;
        color: $white;
        font-weight: 400;
        font-size: 12px;
        line-height: 1;
      }

      a,
      .e-site-link {
        color: $white;
        text-decoration: none;

        &:hover {
          opacity: 0.8;
        }
      }
    }

    .c-gen-nav__user-toggle {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      max-width: 160px;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      font: inherit;
      line-height: 1;
      cursor: pointer;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .c-gen-nav__user-menu {
      position: absolute;
      top: calc(100% + 28px);
      right: 0;
      display: grid;
      min-width: 146px;
      overflow: hidden;
      border-radius: 8px;
      background: rgba(242, 242, 242, 0.95);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
      color: #3d3d3f;
      z-index: 1002;
    }

    .c-gen-nav__user-menu-item {
      min-height: 58px;
      padding: 0 24px;
      border: 0;
      background: transparent;
      color: #3d3d3f;
      font-size: 16px;
      line-height: 1.2;
      text-align: left;
      white-space: nowrap;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        color: #6d9875;
        outline: none;
      }

      &--primary {
        color: #6d9875;
        font-weight: 700;
      }
    }
  }

  // 閻у€熷娑撳顣?
  &.white {
    .c-gen-nav__top {
      background: $blue-night;
      color: $white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .c-gen-nav__menu-link {
      &:hover,
      &.is-active {
        border-bottom-color: #fff;
      }
    }
  }
  // Transparent theme
  &.transparent {
    background: rgba($blue-night, 0.1);

    .c-gen-nav__top {
      box-shadow: none;
    }
    .c-gen-nav__menu-link {
      &:hover,
      &.is-active {
        border-bottom-color: #fff;
      }
    }
  }
  &.green {
    background: $blue-night;
    color: $white;
    .c-gen-nav__menu-link {
      &:hover,
      &.is-active {
        border-bottom-color: #fff;
      }
    }
  }
  // Search results panel
  &__search-results {
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba($white, 0.9);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    min-width: 320px;
    max-width: 480px;
    max-height: 400px;
    overflow-y: auto;
    z-index: 1001;

    @include tablet-landscape {
      min-width: 400px;
    }
  }

  &__search-category {
    padding: 4px 0;
  }

  &__search-item {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    font-size: 14px;
    color: $grey-black !important;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      color: $blue-night !important;
    }

    &--category {
      font-style: italic;
      color: #666;
    }

    &--highlight {
      color: #7ba888;
      font-weight: 500;
    }
  }

  &__search-item-tag {
    color: #7ba888;
    margin-right: 8px;
  }

  &__search-item-text {
    color: #333;
    font-style: normal;
  }

  &__search-divider {
    height: 1px;
    background-color: #eee;
    margin: 8px 0;
  }

  &__search-normal {
    padding: 4px 0;
  }

  &__search-empty {
    padding: 16px;
    text-align: center;
    color: #999;
    font-size: 14px;
  }
}
</style>
